import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { getAuth, signInWithPopup, GoogleAuthProvider, signInWithEmailAndPassword } from 'firebase/auth';
import { Link } from 'react-router-dom';
import { app } from '../firebase';
import {
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  RefreshCcw,
  Lock,
  FileText,
  MessageSquare,
  LogOut,
} from 'lucide-react';
import AdminBlogList from '../components/admin/AdminBlogList';
import AdminBlogEditor from '../components/admin/AdminBlogEditor';

const Admin = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('blog');
  const [blogView, setBlogView] = useState('list'); // 'list' | 'editor'
  const [editingPost, setEditingPost] = useState(null);
  const db = getFirestore(app);
  const auth = getAuth(app);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribeAuth();
  }, [auth]);

  useEffect(() => {
    let unsub = () => {};
    if (user && user.email === 'julian.shapley@p2pdigital.com.au') {
      const q = query(collection(db, 'contact_messages'), orderBy('createdAt', 'desc'));
      unsub = onSnapshot(q, (snap) => {
        setMessages(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate(),
          }))
        );
      });
    }
    return () => unsub();
  }, [db, user]);

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch {
      setError('Invalid email or password');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch {
      setError('Error signing in with Google');
    }
  };

  const updateMessageStatus = async (messageId, newStatus) => {
    try {
      await updateDoc(doc(db, 'contact_messages', messageId), { status: newStatus });
    } catch (err) {
      console.error('Error updating message status:', err);
    }
  };

  const statusColor = (s) =>
    ({
      new: 'bg-blue-500/10 text-blue-400',
      'in-progress': 'bg-amber-500/10 text-amber-400',
      completed: 'bg-emerald-500/10 text-emerald-400',
      archived: 'bg-slate-500/10 text-slate-400',
    }[s] || 'bg-slate-500/10 text-slate-400');

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCcw className="h-6 w-6 text-cyan-400 animate-spin" />
      </div>
    );
  }

  /* ── Auth gate ── */
  if (!user || user.email !== 'julian.shapley@p2pdigital.com.au') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-display font-bold text-white">Admin Access</h1>
            <p className="mt-2 text-slate-400">Sign in to manage your site</p>
          </div>

          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-md p-8">
            <form onSubmit={handleEmailSignIn} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-3">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold hover:opacity-90 transition-opacity"
              >
                Sign In
              </button>

              <div className="text-center">
                <Link to="/forgot-password" className="text-sm text-cyan-400 hover:text-cyan-300">
                  Forgot your password?
                </Link>
              </div>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/[0.06]" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-[#0a0f1a] text-slate-500">or</span>
              </div>
            </div>

            <button
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-white/[0.08] text-slate-300 hover:bg-white/[0.04] transition-colors text-sm"
            >
              <img
                className="h-4 w-4"
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt=""
              />
              Sign in with Google
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Authenticated admin ── */
  return (
    <div className="pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-1 rounded-xl bg-white/[0.03] border border-white/[0.06] p-1">
            {[
              { id: 'blog', label: 'Blog Posts', icon: FileText },
              { id: 'messages', label: 'Messages', icon: MessageSquare },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    if (tab.id === 'blog') setBlogView('list');
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white/[0.08] text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => auth.signOut()}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>

        {/* ── Blog tab ── */}
        {activeTab === 'blog' &&
          (blogView === 'editor' ? (
            <AdminBlogEditor
              post={editingPost}
              onBack={() => {
                setBlogView('list');
                setEditingPost(null);
              }}
            />
          ) : (
            <AdminBlogList
              onEdit={(post) => {
                setEditingPost(post);
                setBlogView('editor');
              }}
              onCreate={() => {
                setEditingPost(null);
                setBlogView('editor');
              }}
            />
          ))}

        {/* ── Messages tab ── */}
        {activeTab === 'messages' && (
          <div>
            <h2 className="text-2xl font-display font-bold text-white mb-2">Contact Messages</h2>
            <p className="text-slate-400 text-sm mb-8">
              {messages.length} message{messages.length !== 1 ? 's' : ''}
            </p>

            {messages.length === 0 ? (
              <div className="text-center py-20 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
                <p className="text-slate-400">No messages yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5 hover:bg-white/[0.05] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <h3 className="text-white font-semibold">
                            {msg.firstName} {msg.lastName}
                          </h3>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${statusColor(
                              msg.status
                            )}`}
                          >
                            {msg.status || 'new'}
                          </span>
                        </div>
                        <p className="text-cyan-400 text-sm mt-0.5">{msg.email}</p>
                        {msg.projectType && (
                          <p className="text-slate-500 text-xs mt-1">{msg.projectType}</p>
                        )}
                        <p className="text-slate-300 text-sm mt-3 leading-relaxed">{msg.message}</p>
                        <p className="text-slate-500 text-xs mt-2 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {msg.createdAt?.toLocaleDateString()}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => updateMessageStatus(msg.id, 'in-progress')}
                          className="p-2 rounded-lg hover:bg-amber-500/10 text-slate-400 hover:text-amber-400 transition-colors"
                          title="In Progress"
                        >
                          <RefreshCcw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => updateMessageStatus(msg.id, 'completed')}
                          className="p-2 rounded-lg hover:bg-emerald-500/10 text-slate-400 hover:text-emerald-400 transition-colors"
                          title="Complete"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => updateMessageStatus(msg.id, 'archived')}
                          className="p-2 rounded-lg hover:bg-white/[0.06] text-slate-400 hover:text-slate-300 transition-colors"
                          title="Archive"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
